/**
 * WORKSPACE LAYOUT + PERSONAL OVERRIDE.
 *
 * Every user boots on the tenant's layout (what an admin last saved); their
 * own saved row is merged on top, key by key, so anything they personally
 * changed still wins for them. Without the workspace layer a colleague's
 * carefully arranged list looked "not persistent" to everyone else — they
 * simply never had a row of their own to load.
 *
 * `leadCardFunctionality` is one flat object. `dynamicCardFunctionality` is a
 * map of routeId -> layout, so it merges one level deeper: a user who has
 * personalised the Enquiry list still inherits the workspace layout on every
 * other stage route.
 */
type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj =>
  Boolean(v) && typeof v === "object" && !Array.isArray(v);

/** Flat merge: workspace underneath, the user's own keys on top. */
export function mergeFlatPreference(
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  if (!isObj(tenantDefault)) return userConfig;
  if (!isObj(userConfig)) return tenantDefault;
  return { ...tenantDefault, ...userConfig };
}

/** Route map merge: per routeId, workspace underneath the user's own keys. */
export function mergeRouteMapPreference(
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  if (!isObj(tenantDefault)) return userConfig;
  if (!isObj(userConfig)) return tenantDefault;
  const out: Obj = { ...tenantDefault };
  for (const [routeId, userRoute] of Object.entries(userConfig)) {
    const tenantRoute = tenantDefault[routeId];
    out[routeId] =
      isObj(tenantRoute) && isObj(userRoute)
        ? { ...tenantRoute, ...userRoute }
        : userRoute;
  }
  return out;
}

/**
 * GLOBALISED LAYOUTS ("Globalise configs", Settings → Manage, 2026-09-09).
 * The workspace layout goes OVER the user's own row: whatever the admin
 * saved is what everyone sees. Only the WORKING state that never belonged
 * to a layout stays personal — the List view's header-menu filters and sort
 * (`listColumnFilters`, `listColumnSort`), which are a person's current
 * slice of the data, not an arrangement. Where the admin has saved nothing
 * for a view there is nothing to impose, so the user's own row stands.
 */
const PERSONAL_WORKING_KEYS = ["listColumnFilters", "listColumnSort"] as const;

function imposeLayout(tenantDefault: Obj, userConfig: unknown): Obj {
  const out: Obj = { ...tenantDefault };
  if (isObj(userConfig)) {
    for (const key of PERSONAL_WORKING_KEYS) {
      if (key in userConfig) out[key] = userConfig[key];
    }
  }
  return out;
}

/** Flat: workspace layout wins; the user keeps only filters/sort. */
export function imposeFlatPreference(
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  if (!isObj(tenantDefault)) return userConfig;
  return imposeLayout(tenantDefault, userConfig);
}

/** Route map: per routeId the workspace layout wins where one exists. */
export function imposeRouteMapPreference(
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  if (!isObj(tenantDefault)) return userConfig;
  if (!isObj(userConfig)) return tenantDefault;
  const out: Obj = { ...userConfig };
  for (const [routeId, tenantRoute] of Object.entries(tenantDefault)) {
    out[routeId] = isObj(tenantRoute)
      ? imposeLayout(tenantRoute, userConfig[routeId])
      : tenantRoute;
  }
  return out;
}

/**
 * Pick the merge for the tenant's setting. `globalised` = the admin's
 * layout is imposed; otherwise the user's own keys win (the default).
 */
export function resolveFlatPreference(
  globalised: boolean,
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  return globalised
    ? imposeFlatPreference(tenantDefault, userConfig)
    : mergeFlatPreference(tenantDefault, userConfig);
}

export function resolveRouteMapPreference(
  globalised: boolean,
  tenantDefault: unknown,
  userConfig: unknown,
): unknown {
  return globalised
    ? imposeRouteMapPreference(tenantDefault, userConfig)
    : mergeRouteMapPreference(tenantDefault, userConfig);
}
